import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Loader2, RotateCcw, CheckCircle2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useI18n } from "@/context/I18nContext";

/**
 * LiveSelfieCapture — modal that streams the user's webcam, lets them grab a
 * frame, retake, and confirm. We intentionally do not allow uploading a
 * gallery photo: the whole point of this control is anti-spoofing.
 *
 * Props:
 *   - open / onOpenChange         : standard shadcn dialog controls
 *   - title / description         : top headings (already localised by caller)
 *   - onCapture(blob, dataUrl)    : invoked when the user confirms
 *   - submitting                  : when true, disables the confirm button
 *   - submitLabel                 : optional override for the confirm CTA
 *   - extra                       : optional children rendered below the preview
 *                                    (used by CheckInModal to attach GPS status)
 */
export default function LiveSelfieCapture({
  open,
  onOpenChange,
  title,
  description,
  onCapture,
  submitting = false,
  submitLabel,
  extra,
}) {
  const { locale } = useI18n();
  const L = (en, es) => (locale === "es" ? es : en);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState("");
  const [captured, setCaptured] = useState(null); // { blob, dataUrl }
  const [starting, setStarting] = useState(false);

  const stopStream = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
  }, [stream]);

  // Boot the camera every time the modal opens
  useEffect(() => {
    if (!open) {
      stopStream();
      setCaptured(null);
      setError("");
      return;
    }
    let cancelled = false;
    (async () => {
      setStarting(true);
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        setStream(s);
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          await videoRef.current.play().catch(() => {});
        }
      } catch (e) {
        const msg = e?.name === "NotAllowedError"
          ? L("Camera permission was denied. Please allow camera access in your browser settings and try again.",
               "Se denegó el permiso de cámara. Permite el acceso en la configuración del navegador y prueba de nuevo.")
          : L("We couldn't access your camera. Make sure no other app is using it.",
               "No pudimos acceder a tu cámara. Asegúrate de que ninguna otra app la esté usando.");
        setError(msg);
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    // Mirror so the captured frame matches what the user sees
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
      setCaptured({ blob, dataUrl });
    }, "image/jpeg", 0.85);
  };

  const confirm = async () => {
    if (!captured) return;
    await onCapture(captured.blob, captured.dataUrl);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="live-selfie-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tighter">{title}</DialogTitle>
          {description && <DialogDescription className="text-sm text-[#4B5563]">{description}</DialogDescription>}
        </DialogHeader>

        <div className="relative aspect-square bg-black/90 rounded-2xl overflow-hidden">
          {error ? (
            <div className="absolute inset-0 grid place-items-center p-6 text-center text-white text-sm" data-testid="selfie-error">
              <div>
                <X className="w-8 h-8 mx-auto mb-2 text-red-400" />
                {error}
              </div>
            </div>
          ) : captured ? (
            <img
              src={captured.dataUrl}
              alt="captured selfie"
              className="w-full h-full object-cover"
              data-testid="selfie-preview"
            />
          ) : (
            <>
              {starting && (
                <div className="absolute inset-0 grid place-items-center text-white">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              )}
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                style={{ transform: "scaleX(-1)" }}
                playsInline
                muted
                data-testid="selfie-video"
              />
            </>
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        {extra && <div className="mt-3">{extra}</div>}

        <div className="flex flex-col sm:flex-row gap-2 justify-end mt-4">
          {captured ? (
            <>
              <Button variant="outline" className="rounded-xl" onClick={() => setCaptured(null)} disabled={submitting} data-testid="selfie-retake">
                <RotateCcw className="w-4 h-4 mr-2" /> {L("Retake", "Repetir")}
              </Button>
              <Button onClick={confirm} disabled={submitting} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="selfie-confirm">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> {submitLabel || L("Confirm", "Confirmar")}</>}
              </Button>
            </>
          ) : (
            <Button onClick={capture} disabled={!stream || starting || !!error} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="selfie-capture">
              <Camera className="w-4 h-4 mr-2" /> {L("Capture", "Capturar")}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
